import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Table, Button, Card, Space, Tag, Input, Select, message, Popconfirm, Dropdown, Tooltip
} from 'antd';
import {
  UserAddOutlined, SearchOutlined, EyeOutlined, DownloadOutlined, StarOutlined,
  StopOutlined, CheckCircleOutlined, UserDeleteOutlined, DeleteOutlined, MoreOutlined,
  SwapOutlined
} from '@ant-design/icons';
import { exportToExcel } from '../../utils/excelExport';
import { useStores } from '../../stores';
import PromoteUserModal from '../../components/PromoteUserModal';
import BackButton from '../../components/BackButton';
import adminPartnerService from '../../services/adminPartnerService';

const { Search } = Input;

const UserList = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { adminPartnerStore, authStore } = useStores();
  const [promoteModalVisible, setPromoteModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [switchingTo, setSwitchingTo] = useState(null);

  const [filters, setFilters] = useState({
    role: null,
    segment: null,
    search: ''
  });
  const [updatingSegment, setUpdatingSegment] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const params = {};
      if (filters.role) params.role = filters.role;
      if (filters.segment) params.segment = filters.segment;
      if (filters.search) params.search = filters.search;

      await adminPartnerStore.fetchAllUsers(params);
    } catch (error) {
      message.error(t('admin.users.loadFailed'));
    }
  };

  const handlePromote = (user) => {
    setSelectedUser(user);
    setPromoteModalVisible(true);
  };

  const handlePromoteSubmit = async (userId, promotionData) => {
    await adminPartnerStore.promoteToPartner(userId, promotionData);
    await loadUsers(); // Reload users to show updated role
  };

  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
  };

  const handleRoleFilter = (value) => {
    setFilters(prev => ({ ...prev, role: value }));
  };

  const handleSegmentFilter = (value) => {
    setFilters(prev => ({ ...prev, segment: value }));
  };

  const handleSegmentChange = async (userId, newSegment) => {
    setUpdatingSegment(userId);
    try {
      await adminPartnerService.updateUser(userId, { segment: newSegment });
      message.success(t('admin.users.segmentUpdated'));
      loadUsers(); // Reload to show updated segment
    } catch (error) {
      message.error(t('admin.users.segmentUpdateFailed'));
    } finally {
      setUpdatingSegment(null);
    }
  };

  const handleDeactivate = async (userId) => {
    try {
      await adminPartnerService.deactivateUser(userId);
      message.success(t('admin.users.deactivateSuccess'));
      loadUsers();
    } catch (error) {
      message.error(error.response?.data?.error || t('admin.users.deactivateFailed'));
    }
  };

  const handleReactivate = async (userId) => {
    try {
      await adminPartnerService.reactivateUser(userId);
      message.success(t('admin.users.reactivateSuccess'));
      loadUsers();
    } catch (error) {
      message.error(error.response?.data?.error || t('admin.users.reactivateFailed'));
    }
  };

  const handleDowngrade = async (userId) => {
    try {
      await adminPartnerService.downgradePartner(userId);
      message.success(t('admin.users.downgradeSuccess'));
      loadUsers();
    } catch (error) {
      message.error(error.response?.data?.error || t('admin.users.downgradeFailed'));
    }
  };

  const handleDelete = async (userId) => {
    try {
      await adminPartnerService.deleteUser(userId);
      message.success(t('admin.users.deleteSuccess'));
      loadUsers();
    } catch (error) {
      message.error(error.response?.data?.error || t('admin.users.deleteFailed'));
    }
  };

  const handleSwitchTo = async (user) => {
    setSwitchingTo(user.id);
    try {
      const result = await adminPartnerService.impersonateUser(user.id);
      authStore.switchToUser(result.user, result.access, result.refresh, result.original_admin);
      message.success(t('admin.impersonation.success', { email: user.email }));
      navigate('/');
    } catch (error) {
      message.error(error.response?.data?.error || t('admin.impersonation.failed'));
    } finally {
      setSwitchingTo(null);
    }
  };

  const getSegmentBadge = (segment) => {
    const config = {
      VIP: { color: 'gold', icon: <StarOutlined /> },
      MEDIUM: { color: 'blue', icon: null },
      LOW: { color: 'default', icon: null },
      NONE: { color: 'default', icon: null }
    };
    const { color, icon } = config[segment] || config.NONE;
    return (
      <Tag color={color}>
        {icon} {t(`admin.users.segments.${segment?.toLowerCase() || 'none'}`)}
      </Tag>
    );
  };

  const getRoleBadge = (role) => {
    const colors = {
      CLIENT: 'blue',
      PARTNER: 'purple',
      ADMIN: 'red'
    };
    return <Tag color={colors[role] || 'default'}>{role}</Tag>;
  };

  const handleExport = () => {
    const exportColumns = [
      { title: 'ID', dataIndex: 'id' },
      { title: t('common.email'), dataIndex: 'email' },
      { title: t('admin.users.firstName'), dataIndex: 'first_name' },
      { title: t('admin.users.lastName'), dataIndex: 'last_name' },
      {
        title: t('admin.users.company'),
        dataIndex: 'partner_profile',
        exportRender: (profile) => profile?.store_name || ''
      },
      { title: t('admin.users.role'), dataIndex: 'role' },
      { title: t('admin.users.segment'), dataIndex: 'segment' },
      {
        title: t('admin.users.joinedDate'),
        dataIndex: 'date_joined',
        exportRender: (date) => date ? new Date(date).toLocaleDateString() : ''
      }
    ];
    exportToExcel(adminPartnerStore.users, exportColumns, 'users', t('admin.users.title'));
    message.success(t('admin.users.exportSuccess'));
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      fixed: 'left',
    },
    {
      title: t('common.email'),
      dataIndex: 'email',
      key: 'email',
      width: 200,
      ellipsis: true,
    },
    {
      title: t('common.name'),
      key: 'name',
      width: 150,
      ellipsis: true,
      render: (_, record) => `${record.first_name || ''} ${record.last_name || ''}`.trim() || '-',
    },
    {
      title: t('admin.users.company'),
      key: 'company',
      width: 150,
      ellipsis: true,
      render: (_, record) => record.partner_profile?.store_name || '-',
    },
    {
      title: t('admin.users.role'),
      dataIndex: 'role',
      key: 'role',
      width: 90,
      render: (role) => getRoleBadge(role),
    },
    {
      title: t('admin.users.segment'),
      dataIndex: 'segment',
      key: 'segment',
      width: 130,
      render: (segment, record) => (
        <Select
          value={segment || 'NONE'}
          onChange={(value) => handleSegmentChange(record.id, value)}
          loading={updatingSegment === record.id}
          size="small"
          style={{ width: 110 }}
        >
          <Select.Option value="VIP">
            <StarOutlined style={{ color: 'gold' }} /> VIP
          </Select.Option>
          <Select.Option value="MEDIUM">{t('admin.users.segments.medium')}</Select.Option>
          <Select.Option value="LOW">{t('admin.users.segments.low')}</Select.Option>
          <Select.Option value="NONE">{t('admin.users.segments.none')}</Select.Option>
        </Select>
      ),
    },
    {
      title: t('admin.users.status'),
      dataIndex: 'is_active',
      key: 'is_active',
      width: 90,
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? t('admin.users.active') : t('admin.users.inactive')}
        </Tag>
      ),
    },
    {
      title: t('admin.users.joinedDate'),
      dataIndex: 'date_joined',
      key: 'date_joined',
      width: 100,
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => {
        const menuItems = [];

        // Deactivate/Reactivate
        if (record.role !== 'ADMIN') {
          if (record.is_active) {
            menuItems.push({
              key: 'deactivate',
              icon: <StopOutlined />,
              label: t('admin.users.deactivate'),
              danger: true,
            });
          } else {
            menuItems.push({
              key: 'reactivate',
              icon: <CheckCircleOutlined />,
              label: t('admin.users.reactivate'),
            });
          }
        }

        // Delete (not for admins)
        if (record.role !== 'ADMIN') {
          menuItems.push({
            type: 'divider',
          });
          menuItems.push({
            key: 'delete',
            icon: <DeleteOutlined />,
            label: t('admin.users.deleteUser'),
            danger: true,
          });
        }

        const handleMenuClick = ({ key }) => {
          if (key === 'deactivate') {
            handleDeactivate(record.id);
          } else if (key === 'reactivate') {
            handleReactivate(record.id);
          }
          // Delete requires confirmation, handled separately
        };

        return (
          <Space>
            {/* Switch To button - only for non-admin users */}
            {record.role !== 'ADMIN' && record.is_active && (
              <Tooltip title={t('admin.impersonation.switchTo')}>
                <Button
                  size="small"
                  icon={<SwapOutlined />}
                  onClick={() => handleSwitchTo(record)}
                  loading={switchingTo === record.id}
                  style={{ color: '#722ed1', borderColor: '#722ed1' }}
                >
                  {t('admin.impersonation.switchTo')}
                </Button>
              </Tooltip>
            )}
            {record.role === 'CLIENT' && (
              <Button
                type="primary"
                size="small"
                icon={<UserAddOutlined />}
                onClick={() => handlePromote(record)}
              >
                {t('admin.users.promoteToPartner')}
              </Button>
            )}
            {record.role === 'PARTNER' && record.partner_profile && (
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={() => navigate(`/admin/partners/${record.partner_profile.id}`)}
              >
                {t('admin.users.viewPartnerProfile')}
              </Button>
            )}
            {record.role === 'PARTNER' && (
              <Popconfirm
                title={t('admin.users.downgradeConfirmTitle')}
                description={t('admin.users.downgradeConfirmDesc')}
                onConfirm={() => handleDowngrade(record.id)}
                okText={t('common.yes')}
                cancelText={t('common.no')}
                okButtonProps={{ danger: true }}
              >
                <Button
                  size="small"
                  danger
                  icon={<UserDeleteOutlined />}
                >
                  {t('admin.users.downgradeToClient')}
                </Button>
              </Popconfirm>
            )}
            {record.role === 'ADMIN' && (
              <Tag color="gold">{t('admin.users.adminUser')}</Tag>
            )}
            {record.role !== 'ADMIN' && (
              <Dropdown
                menu={{
                  items: menuItems.filter(item => item.key !== 'delete'),
                  onClick: handleMenuClick,
                }}
                trigger={['click']}
              >
                <Button size="small" icon={<MoreOutlined />} />
              </Dropdown>
            )}
            {record.role !== 'ADMIN' && (
              <Popconfirm
                title={t('admin.users.deleteConfirmTitle')}
                description={t('admin.users.deleteConfirmDesc')}
                onConfirm={() => handleDelete(record.id)}
                okText={t('common.yes')}
                cancelText={t('common.no')}
                okButtonProps={{ danger: true }}
              >
                <Tooltip title={t('admin.users.deleteUser')}>
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Tooltip>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <BackButton to="/admin/dashboard" label={t('admin.backToDashboard')} />
      <Card
        title={
          <span style={{ fontSize: '20px', fontWeight: 'bold' }}>
            {t('admin.users.title')}
          </span>
        }
      >
        {/* Filters */}
        <Space style={{ marginBottom: 16 }} size="middle">
          <Search
            placeholder={t('admin.users.searchPlaceholder')}
            onSearch={handleSearch}
            onChange={(e) => !e.target.value && handleSearch('')}
            style={{ width: 300 }}
            prefix={<SearchOutlined />}
          />

          <Select
            placeholder={t('admin.users.filterByRole')}
            style={{ width: 180 }}
            allowClear
            value={filters.role}
            onChange={handleRoleFilter}
          >
            <Select.Option value="CLIENT">{t('admin.users.roles.clients')}</Select.Option>
            <Select.Option value="PARTNER">{t('admin.users.roles.partners')}</Select.Option>
            <Select.Option value="ADMIN">{t('admin.users.roles.admins')}</Select.Option>
          </Select>

          <Select
            placeholder={t('admin.users.filterBySegment')}
            style={{ width: 150 }}
            allowClear
            value={filters.segment}
            onChange={handleSegmentFilter}
          >
            <Select.Option value="VIP"><StarOutlined style={{ color: 'gold' }} /> VIP</Select.Option>
            <Select.Option value="MEDIUM">{t('admin.users.segments.medium')}</Select.Option>
            <Select.Option value="LOW">{t('admin.users.segments.low')}</Select.Option>
            <Select.Option value="NONE">{t('admin.users.segments.none')}</Select.Option>
          </Select>

          <Button type="primary" onClick={loadUsers}>
            {t('admin.applyFilters')}
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExport}
            disabled={!adminPartnerStore.users.length}
          >
            {t('admin.exportExcel')}
          </Button>
        </Space>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={adminPartnerStore.users}
          rowKey="id"
          loading={adminPartnerStore.loading}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Promote User Modal */}
      <PromoteUserModal
        visible={promoteModalVisible}
        user={selectedUser}
        onClose={() => {
          setPromoteModalVisible(false);
          setSelectedUser(null);
        }}
        onPromote={handlePromoteSubmit}
      />
    </div>
  );
});

export default UserList;
